import React, { useEffect, useState } from "react";
import { pdfjs } from "react-pdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import DragDropComponent from "../CommonComponents/DragDropComponent/DragDropComponent";
import PageLimitConfirmModal from "./PageLimitConfirmModal";
import ReplaceConfirmModal from "./ReplaceConfirmModal";
import "./UploadKdfModal.css";
import { quality_data } from "../../hooks/utils";
import apiGETRequest from "../../utils/apiRequest";
import { Col, Container, ProgressBar, Row } from "react-bootstrap";
import Button from "../Elements/Button";
import { csrfToken } from "../../utils/csrfTokenUtils";
import CustomDropdown from "../CommonComponents/CustomDropDown/CustomDropDown";
import "../../../assets/stylesheets/flags.css";

pdfjs.GlobalWorkerOptions.workerSrc = "/worker.js";

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const PAGE_COUNT_WARNING_THRESHOLD = 50;

const UploadKdfModal = ({
  closeModal,
  userId,
  docId,
  refreshPDFList,
  selectedFile = null,
}) => {
  const [progressCount, setProgressCount] = useState(0);
  const [loading, setLoading] = useState(false)
  const [file, setFile] = useState("");
  const [fileError, setFileError] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPageCount, setPendingPageCount] = useState(null);
  const [replaceInfo, setReplaceInfo] = useState(null);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [payload, setPayload] = useState(() => ({
    language: selectedFile == null ? "en-US" : (selectedFile?.language?.locale || ""),
    quality: quality_data()[0]?.value ?? "",
  }));
  const [languages, setLanguages] = useState([]);

  useEffect(() => {
    document.body.classList.toggle("page-limit-confirm-open", !!pendingFile);
    return () => document.body.classList.remove("page-limit-confirm-open");
  }, [pendingFile]);

  useEffect(() => {
    document.body.classList.toggle("replace-confirm-open", showReplaceConfirm);
    return () => document.body.classList.remove("replace-confirm-open");
  }, [showReplaceConfirm]);

  const handleFileRejected = (fileRejections) => {
    const codes = fileRejections[0]?.errors?.map((err) => err.code) || [];
    if (codes.includes("file-too-large")) {
      setFileError("File exceeds the 25MB size limit.");
    } else if (codes.includes("file-invalid-type")) {
      setFileError("Only PDF files are allowed.");
    } else {
      setFileError("Unable to upload this file.");
    }
  };

  const validateAndSetFile = async (accepted) => {
    setFileError("");
    const pdfFile = accepted?.[0];
    if (!pdfFile) return;

    if (pdfFile.size > MAX_FILE_SIZE_BYTES) {
      setFileError("File exceeds the 25MB size limit.");
      return;
    }

    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdfDoc = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      if (pdfDoc.numPages > PAGE_COUNT_WARNING_THRESHOLD) {
        setPendingFile(accepted);
        setPendingPageCount(pdfDoc.numPages);
        return;
      }
    } catch (error) {
    }
    setFile(accepted);
  };

  const fileErrorBanner = fileError && (
    <div className="upload-file-error">
      <FontAwesomeIcon icon={faTriangleExclamation} />
      <span>{fileError}</span>
    </div>
  );

  const handlePageLimitConfirm = (proceed) => {
    if (proceed && pendingFile) {
      setFile(pendingFile);
    } else {
      setFileError("No problem — feel free to choose a different PDF.");
    }
    setPendingFile(null);
    setPendingPageCount(null);
  };

  const UploadKdf = async (skipConfirm = false) => {
    const languageValue = payload.language || selectedFile?.language?.locale || "";
    if (!languageValue) {
      setFileError("Please select a language.");
      return;
    }
    if (
      skipConfirm !== true &&
      selectedFile?.replace &&
      replaceInfo?.needs_confirm
    ) {
      setShowReplaceConfirm(true);
      return;
    }

    setProgressCount(10);
    setLoading(true);

    const formData = new FormData();
    formData.append("authenticity_token", csrfToken());
    formData.append("docs_file[doc_id]", docId);
    formData.append("docs_file[user_id]", userId);
    formData.append("docs_file[file_cache]", "");
    formData.append(
          "docs_file[replace]",
          selectedFile ? selectedFile.replace : "false"
      );
      formData.append("docs_file[language]", languageValue);
    formData.append("docs_file[quality]", payload.quality);
    formData.append("docs_file[file]", file[0]);
    if (selectedFile != null) {
      formData.append("_method", "patch");
    }

    try {
      // Optimize: Use requestAnimationFrame for smoother progress updates
      requestAnimationFrame(() => setProgressCount(50));
      
      const response = await fetch(
        selectedFile != null
          ? `/docs/${docId}/files/${selectedFile?.id}.json`
          : `/docs/${docId}/files.json`,
        {
          method: "POST",
          body: formData,
          cookie: {
            "X-CSRF-Token": csrfToken(),
          },
        }
      );
      
      if (response.ok) {
        requestAnimationFrame(() => {
          setProgressCount(100);
          // Close modal and refresh immediately without waiting
          closeModal(false);
          refreshPDFList(true);
        });
      } else {
        setProgressCount(0);
        setLoading(false);
        try {
          const data = await response.json();
          setFileError(
            data?.language?.[0]
              ? `Language ${data.language[0]}.`
              : data?.file?.[0] || "Upload failed. Please try again."
          );
        } catch (error) {
          setFileError("Upload failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setProgressCount(0);
      setLoading(false);
    }
  };

  const handleOnchangeEvent = (e) => {
    setPayload((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const languages = await apiGETRequest(
          selectedFile != null
            ? `docs/${docId}/languages/${selectedFile.id}.json`
            : `docs/${docId}/languages.json`
        );
        const languageArray = Object.values(languages);
        setLanguages(languageArray);
        // The initial state already defaults to en-US (add) / the previous
        // language (replace); only correct it if that default turned out to
        // already be taken by another language edition, without making the
        // user wait on this fetch to be able to hit Upload.
        if (selectedFile == null) {
          setPayload((prev) => {
            const stillValid = languageArray.some((lan) => lan.locale === prev.language);
            return stillValid ? prev : { ...prev, language: languageArray[0]?.locale || "" };
          });
        }
      } catch (error) {
        // console.error('Error fetching languages:', error);
      }
    };
    fetchLanguages();
  }, []);

  useEffect(() => {
    if (!selectedFile?.replace || !selectedFile?.id) return;
    let cancelled = false;
    apiGETRequest(`docs/${docId}/files/${selectedFile.id}/replace_info.json`)
      .then((info) => {
        if (!cancelled) setReplaceInfo(info);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [docId, selectedFile]);

  return (
    <>
      <div className="domain-main">
        {!file ? (
          <>
            <DragDropComponent setFile={validateAndSetFile} onReject={handleFileRejected} />
            {fileErrorBanner}
          </>
        ) : (
          <Container className="mb-0">
            <Row>
              <Col md={4}>
                <p>{file[0]?.path.split("/")[1]}</p>
                <p>{(file[0]?.size / 1024)?.toFixed(2)} KB</p>
              </Col>
              <Col md={4}>
                <CustomDropdown
                  options={languages?.map((lan) => {
                    return {
                      value: lan.locale,
                      label: lan.name,
                      class: lan.class,
                    };
                  })}
                  name={"language"}
                  onSelect={(e) =>
                    handleOnchangeEvent({
                      target: { name: "language", value: e },
                    })
                  }
                  selected={payload.language == '' ? selectedFile?.language : payload?.language}
                  placeholder="Select an Language"
                />
              </Col>
              <Col md={4}>
                <CustomDropdown
                  options={quality_data()?.map((quality) => {
                    return {
                      value: quality.value,
                      label: quality.name,
                    };
                  })}
                  name={"quality"}
                  onSelect={(e) =>
                    handleOnchangeEvent({
                      target: { name: "quality", value: e },
                    })
                  }
                  selected={payload.quality}
                  placeholder="Select a Quality"
                />
              </Col>
            </Row>
            <Col className="text-center">
              {progressCount > 0 && (
                <ProgressBar
                  className={`my-4`}
                  now={progressCount}
                  label={`${progressCount}%`}
                  striped
                  variant="success"
                />
              )}
              {fileErrorBanner}
              <Button
                text={"Upload"}
                addonClass="p-5"
                onClick={() => UploadKdf()}
                classList={loading || !(payload.language || selectedFile?.language?.locale) ? "disabled" : "success"}
              />
            </Col>
          </Container>
        )}
      </div>
      <PageLimitConfirmModal
        show={!!pendingFile}
        pageCount={pendingPageCount}
        onConfirm={() => handlePageLimitConfirm(true)}
        onCancel={() => handlePageLimitConfirm(false)}
      />
      <ReplaceConfirmModal
        show={showReplaceConfirm}
        info={replaceInfo}
        onConfirm={() => {
          setShowReplaceConfirm(false);
          UploadKdf(true);
        }}
        onCancel={() => setShowReplaceConfirm(false)}
      />
    </>
  );
};

export default React.memo(UploadKdfModal);
